import {ErrorCallout} from '/src/com/yext/listingslaunchstorm/js/enterprise/errorcallout/errorcallout';
import {GbpGroup} from '/src/com/yext/listingslaunchstorm/js/enterprise/setuppublisher/data/gbpgroup';
import {LinkedAccount} from '/src/com/yext/listingslaunchstorm/js/enterprise/setuppublisher/data/linkedaccount';
import {useGbpGroupLocations} from '/src/com/yext/listingslaunchstorm/js/enterprise/setuppublisher/datasource/usegbpgrouplocations';
import {useGbpGroups} from '/src/com/yext/listingslaunchstorm/js/enterprise/setuppublisher/datasource/usegbpgroups';
import {GbpGroupInput} from '/src/com/yext/listingslaunchstorm/js/enterprise/setuppublisher/gbp/gbpgroupinput';
import {ContentSkeleton} from '/ui/components/contentskeleton/contentskeleton';
import {MenuController} from '/ui/components/menu/menucontroller';
import {Table} from '/ui/components/table/table';

import * as styles from '/src/com/yext/listingslaunchstorm/js/enterprise/setuppublisher/gbp/gbp.module.scss';

/**
 * @param {{
*  linkedAccount: LinkedAccount,
*  groupId: (string | undefined),
*  setGroupConfiguration: function(string=): void,
*  disabled: boolean,
* }} props
*/
export function GbpGroupMenu({linkedAccount, groupId, setGroupConfiguration, disabled}) {
  const [gbpGroups, setGbpGroups] = React.useState([]);
  const gbpGroupsState = useGbpGroups(linkedAccount.id);
  const gbpGroupLocationsState = useGbpGroupLocations(linkedAccount.id, gbpGroupsState.data?.groups);

  React.useEffect(() => {
    if (gbpGroupsState.status === 'success' && gbpGroupsState.data) {
      setGbpGroups(gbpGroupsState.data.groups);
    }
  }, [gbpGroupsState.status, gbpGroupsState.data]);

  React.useEffect(() => {
    if (gbpGroupLocationsState.status === 'success' && gbpGroupLocationsState.data?.gbpAccountToLocationCount !== undefined) {
      setGbpGroups(prevGroups =>
        prevGroups.map(gbpGroup => ({
          ...gbpGroup,
          locationCount: gbpGroupLocationsState.data?.gbpAccountToLocationCount.get(gbpGroup.id) ?? 0,
        })),
      );
    }
  }, [gbpGroupLocationsState.data, gbpGroupLocationsState.status]);

  let content;

  if (gbpGroupsState.status === 'error') {
    content = <ErrorCallout onReload={gbpGroupsState.refetch} />;
  } else if (gbpGroupsState.status !== 'success' || gbpGroupsState.data === undefined || gbpGroups.length === 0) {
    content = <ContentSkeleton type="box" height={100} />;
  } else if (gbpGroupsState.data.useTextInput) {
    content = <GbpGroupInput
      linkedAccount={linkedAccount}
      groupId={groupId}
      setGroupConfiguration={setGroupConfiguration}
      fetchedGroupIds={gbpGroups.map(gbpGroup => gbpGroup.id)}
      disabled={disabled}
    />;
  } else {
    const gbpGroupActions = gbpGroups.map(gbpGroup => ({
      name: getGroupName(gbpGroup),
      callback: () => setGroupConfiguration(gbpGroup.id),
    }));

    content = <Table
      columns={getColumns()}
      dataRows={[
        {
          id: 'groupIdMenu',
          [COLUMN_IDS.GROUP_ID]: (
            <MenuController
              allowContainerOverflow
              actions={gbpGroupActions}
              label={getLabel(groupId, gbpGroups)}
              disabled={disabled}
            />
          ),
        },
      ]}
      label={yext.msg('Google Location Group ID Menu')}
    />;
  }

  return <div className={styles.gbpGroupMenu}>{content}</div>;
}

const COLUMN_IDS = {
  GROUP_ID: 'groupId',
};

function getColumns() {
  return [
    {
      id: COLUMN_IDS.GROUP_ID,
      label: yext.msg('Google Location Group ID'),
    },
  ];
}

/**
 * @param {string | undefined} groupId
 * @param {!Array<GbpGroup>} gbpGroups
 */
function getLabel(groupId, gbpGroups) {
  const selectedGroup = gbpGroups.find(gbpGroup => gbpGroup.id === groupId);
  if (groupId === undefined || selectedGroup === undefined) {
    return yext.msg('Select Google Location Group ID');
  }
  return getGroupName(selectedGroup);
}


function getGroupName(gbpGroup) {
  if (gbpGroup?.locationCount > 0) {
    return `${gbpGroup.name} (${gbpGroup.id}) (${gbpGroup.locationCount} locations)`;
  }
  return `${gbpGroup.name} (${gbpGroup.id})`;
}
